import { ref, computed, watch, getCurrentInstance } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import router from '@/router'
import ability from '@/libs/acl/ability'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import { useTranslates } from "@/utils/useTranslates";

export default function useList() {
  const refListTable = ref(null)
  const vm = getCurrentInstance();
  const proxy = vm.root.proxy;

  const toast = useToast()
  const { t } = useI18nUtils();

  store.dispatch('hanwha_dvr/getAll')
  store.dispatch('center/getAll')
  store.dispatch('sector/getAll')
  store.dispatch('tenant/getAll')
  store.dispatch('camera_type/getAll')
  store.dispatch('unit/getAll', { with: 'router:id,id_tenant' })
  store.dispatch('omniview_reseller/getAll')
  store.dispatch('unit/getAll')
  store.dispatch('tenant/getDeviceTags')

  const dvrOptions = computed(() => store.getters['hanwha_dvr/getSelectedItems'])
  const dvrFilter = ref(null)
  const centerOptions = computed(() => store.getters['center/getSelectedItems'])
  const centerFilter = ref(null)
  const tenantOptions = computed(() => store.getters['tenant/getSelectedItems'])
  const tenantFilter = ref(null)
  const sectorOptions = computed(() => store.getters['sector/getSelectedItems'])
  const sectorFilter = ref(null)
  const cameraTypeOptions = computed(() => store.getters['camera_type/getSelectedItems'].filter(ct => ct.label !== 'FISHEYE'))
  const unitOptions = computed(() => store.getters['unit/getSelectedItems'])
  const unitFilter = ref(null)
  const deviceTagFilter = ref(null)
  const onlineFilter = ref(null)
  const rtspAvailableFilter = ref(null)
  const isFisheyeFilter = ref(null)
  const sendToSeprobanFilter = ref(null)
  const resellerOptions = computed(() => store.getters['omniview_reseller/getSelectedItems'])
  const deviceTagOptions = computed(() =>
    (store.getters['tenant/getDeviceTags'] || []).map(tag => ({
      value: tag.id,
      label: tag.name,
    }))
  )

  const translations = {
    "hanwha_camera.confirm_delete": t("hanwha_camera.confirm_delete"),
    "actions.delete": t("actions.delete"),
    "actions.cancel": t("actions.cancel"),
    "messages.delete.body": t("messages.delete.body")
  };

  const routeDvr = router.currentRoute.query.dvr
  if (routeDvr !== undefined && routeDvr !== null) {
    const routeDvrIds = Array.isArray(routeDvr) ? routeDvr : [routeDvr]
    dvrFilter.value = routeDvrIds
      .map(id => Number(id))
      .filter(id => Number.isInteger(id) && id > 0)
  }

  const tableColumns = [
    { label: 'hanwha_camera.name', key: 'name', sortable: true },
    { label: 'hanwha_camera.channel', key: 'channel', sortable: true },
    { label: 'hanwha_dvr.title', key: 'hanwha_dvr.name', sortable: false },
    { label: 'device_tag.title', key: 'device_tag.name', sortable: false },
    {
      label: 'tenants.title', key: 'tenants', sortable: false,
      formatter: (value, key, item) => {
        if (item.units) return [...new Set(item.units.map(item => { return item.router?.tenant?.name }))].join(', ')
        return ""
      }
    },
    {
      label: 'unit.title', key: 'units', sortable: false,
      formatter: (value, key, item) => {
        if (item.units) return [...new Set(value.map(item => { return item.description }))].join(', ')
        return ""
      }
    },
    { label: 'camera_proxy.send_to_seproban', key: 'send_to_seproban', sortable: false },
    { label: 'Fisheye', key: 'is_fisheye', sortable: false },
    { label: 'centers.title', key: 'center.name', sortable: false },
    { label: 'reseller.title', key: 'reseller_name', sortable: false },
    { label: 'hanwha_camera.type', key: 'camera_type', sortable: true },
    { label: 'hanwha_camera.model', key: 'model', sortable: true },
    { label: 'hanwha_camera.protocol', key: 'protocol', sortable: true },
    { label: 'hanwha_camera.local_ip', key: 'local_ip', sortable: true },
    { label: 'camera.previous_time_of_prealarma', key: 'delayed_record_minutes', sortable: true },
    { label: 'camera.later_time_of_prealarma', key: 'forward_record_minutes', sortable: true },
    { label: 'camera_proxy.rtsp_available', key: 'device.rtsp_available', sortable: false },
    { label: 'date.created_at', key: 'created_at', sortable: true, formatter: 'formatDateAssigned' },
    { label: 'date.updated_at', key: 'updated_at', sortable: true, formatter: 'formatDateAssigned' },
  ]

  if (ability.can('update', 'HanwhaCamera') == true || ability.can('delete', 'HanwhaCamera') == true) {
    tableColumns.splice(0, 0, { label: 'actions.name', sortable: false, key: 'actions' })
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const fetchList = (ctx, callback) => {
    store.dispatch('hanwha_camera/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      dvr: dvrFilter.value,
      center: centerFilter.value,
      sector: sectorFilter.value,
      tenant: tenantFilter.value,
      units: unitFilter.value,
      device_tag: deviceTagFilter.value,
      online: onlineFilter.value,
      rtsp_available: rtspAvailableFilter.value,
      is_fisheye: isFisheyeFilter.value,
      send_to_seproban: sendToSeprobanFilter.value,
    })
      .then(response => {
        callback(response.data)
        totalList.value = response.meta.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const formatDateAssigned = value => {
    const formattedDate = moment(value).format('DD/MM/YYYY HH:mm:ss')
    return formattedDate
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage, ], () => {
    refetchData()
  })

  watch([ searchQuery ], debounce(() => {
    refetchData()
  }, 500))

  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      dvr: dvrFilter.value,
      center: centerFilter.value,
      sector: sectorFilter.value,
      tenant: tenantFilter.value,
      units: unitFilter.value,
      device_tag: deviceTagFilter.value,
      online: onlineFilter.value,
      rtsp_available: rtspAvailableFilter.value,
      is_fisheye: isFisheyeFilter.value,
      send_to_seproban: sendToSeprobanFilter.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    dvrFilter.value = null;
    centerFilter.value = null;
    sectorFilter.value = null;
    tenantFilter.value = null;
    unitFilter.value = null;
    deviceTagFilter.value = null;
    onlineFilter.value = null;
    rtspAvailableFilter.value = null;
    isFisheyeFilter.value = null;
    sendToSeprobanFilter.value = null;
    buildFilters();
  }

  async function deleteCamera(camera) {
    const confirmation = await confirmDeleteCamera(camera)
    if (!confirmation.isConfirmed) return;
    store.dispatch("hanwha_camera/delete", camera.id).then((response) => {
      if (response.success) {
        contentToast(response.message, "success")
        refetchData()
      } else {
        contentToast(response.message, "danger")
      }
    })
    .catch((response) => {
      const errorMessage = response?.response?.data?.message ?? "Error on delete camera";
      contentToast(errorMessage, "danger")
    });
  }

  const confirmDeleteCamera = async (camera) => {
    const $swal = proxy?.$swal;

    return await $swal({
      title: useTranslates(translations["hanwha_camera.confirm_delete"], { camera: camera.name }),
      text: translations["messages.delete.body"],
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: translations["actions.cancel"],
      confirmButtonText: translations["actions.delete"],
      customClass: {
        confirmButton: "btn btn-primary text-capitalize",
        cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
      },
      buttonsStyling: false,
    });
  };

  function contentToast (message, type) {
    toast({
      component: ToastificationContent,
      props: {
        title: message,
        icon: "CheckIcon",
        variant: type,
      },
    });
  }

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    dvrOptions,
    dvrFilter,
    centerOptions,
    centerFilter,
    sectorOptions,
    sectorFilter,
    tenantOptions,
    tenantFilter,
    cameraTypeOptions,
    unitOptions,
    unitFilter,
    deviceTagOptions,
    deviceTagFilter,
    onlineFilter,
    rtspAvailableFilter,
    isFisheyeFilter,
    sendToSeprobanFilter,
    refetchData,
    filtersCount,
    buildFilters,
    clearSearchFilters,
    deleteCamera,
    resellerOptions
  }
}
